import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { claimActions, httpRequestsOnSuccessActions, systemActions } from 'store';
import { AccordionFullTable, CustomDelete, MinLoader, SelectActions } from 'components';
import { claimTabEnums as claimStatusEnums, pendingClaimTableInfo } from './constants';
import { Colors, FindSuccessItem, Images, PermissionList, RolePermission, token, useModal } from 'utils';
import { BillingTotalBox, SelectSubmissionMethod } from './core';
import axios from 'axios';
import { ReceivableBillTable } from 'fragments';

const GENERATE_ACTION = 'GENERATE_CLAIM';
const DELETE_CLAIM = 'DELETE_CLAIM';
const GET_PENDING_CLAIMS_TOTAL = 'GET_PENDING_CLAIMS_TOTAL';

export const PendingClaims = () => {
    const { open, close } = useModal();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const claimsData = useSelector((state) => state.claim.claims);
    const pendingClaimsTotal = useSelector((state) => state.claim.pendingClaimsTotal);
    const [selectedList, setSelectedList] = useState([]);
    const filteredList = claimsData?.claims;
    const generateClaimSuccess = FindSuccessItem(GENERATE_ACTION);
    const [loadPreview, setLoadPreview] = useState(false);

    const renderParams = () => {
        const sendInfo = {
            ...info,
            status: claimStatusEnums?.PENDING,
        };
        delete sendInfo.page;
        delete sendInfo.tabType;
        delete sendInfo.clientFull;
        // Display-only companion for `payer` — forwarding it sends the payer's NAME as a
        // query param alongside its id. billableAppt.js already stripped it; these did not.
        delete sendInfo.funderName;
        sendInfo.skip = info?.skip ? info?.skip : 0;
        sendInfo.limit = 50;
        return sendInfo;
    };

    useEffect(() => {
        getPendingClaim();
    }, [info]);

    useEffect(() => {
        if (generateClaimSuccess) {
            getPendingClaim('noLoad');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(GENERATE_ACTION));
            close();
        }
    }, [generateClaimSuccess]);

    const getPendingClaim = (loading) => {
        dispatch(claimActions.getPendingClaims({ ...renderParams() }, loading));

        const totalParams = {
            ...renderParams(),
        };
        delete totalParams.skip;
        delete totalParams.limit;

        dispatch(claimActions.getPendingClaimsTotal(totalParams, loading));
    };

    const handleCheck = (ev, item, name) => {
        // if (name === 'selectAll') {
        //     let allList = filteredList?.map((i) => i?.id);
        //     setSelectedList(allList);
        // } else {
        if (name === 'all' || name === 'selectAll') {
            // setSelectedList(filteredList?.map((i) => i?.id));
            setSelectedList(filteredList?.length === selectedList?.length ? [] : filteredList?.map((i) => i?.id));
        } else {
            setSelectedList((prev) => (prev.includes(item?.id) ? prev.filter((id) => id !== item?.id) : [...prev, item?.id]));
        }
    };

    const removeSelectedList = () => {
        setSelectedList([]);
    };

    const afterSuccess = () => {
        getPendingClaim('noLoad');
    };

    const handlePreview = () => {
        setLoadPreview(true);
        axios({
            url: `/claim/1500Forms/PDF`,
            method: 'POST',
            responseType: 'blob',
            headers: {
                'access-token': `${token()}`,
                'Content-Type': 'application/json',
            },
            data: JSON.stringify({ claimIds: selectedList }),
        })
            .then((response) => {
                setLoadPreview(false);
                const fileBlob = new Blob([response?.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(fileBlob);
                window.open(url, '_blank', 'noopener,noreferrer');
            })
            .catch(() => {
                setLoadPreview(false);
            });
    };

    const handleAction = (item, name) => {
        if (name === 'delete') {
            open(
                <CustomDelete
                    info="Delete Claim?"
                    text="This action will permanently remove the claim and its billing information. It cannot be undone."
                    handleDel={() => dispatch(claimActions.deleteClaim({ claimIds: [item?.id] }))}
                    actionType={DELETE_CLAIM}
                    afterSuccess={() => {
                        afterSuccess();
                        removeSelectedList();
                    }}
                    body={
                        <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                            <img style={{ marginTop: 4 }} src={Images.authFile} alt="icon" />
                            <p style={{ fontWeight: 600 }}>{item?.displayId}</p>
                        </div>
                    }
                />
            );
        }
    };

    return (
        <div>
            {!!selectedList?.length && RolePermission([PermissionList.CLAIMS_UPDATE?.code]) && (
                <SelectActions
                    selectedList={selectedList?.length}
                    selectAll={handleCheck}
                    selectInnetText={selectedList?.length === filteredList?.length ? 'Clear Selection' : 'Select All'}
                    submitClaims={() =>
                        open(
                            <SelectSubmissionMethod
                                selectedList={selectedList}
                                afterSuccess={() => {
                                    getPendingClaim('noLoad');
                                    removeSelectedList();
                                }}
                            />
                        )
                    }
                    removeSelected={removeSelectedList}
                    renderOtherBtns={
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            <button onClick={handlePreview} className="select-all-btn">
                                {loadPreview ? (
                                    <MinLoader margin={'0'} color={Colors.TextWhite} position={'relative'} />
                                ) : (
                                    <img src={Images.eyeWhite} alt="show" />
                                )}
                                <p>Preview</p>
                            </button>

                            <button
                                className="red-trash-btn"
                                onClick={() =>
                                    open(
                                        <CustomDelete
                                            info="Delete Selected Claims?"
                                            text="This action will permanently delete all selected claims and their billing information. This cannot be undone."
                                            handleDel={() => dispatch(claimActions.deleteClaim({ claimIds: selectedList }))}
                                            actionType={DELETE_CLAIM}
                                            afterSuccess={() => {
                                                afterSuccess();
                                                removeSelectedList();
                                            }}
                                            body={
                                                <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                                                    <img style={{ marginTop: 4 }} src={Images.authFile} alt="icon" />
                                                    <p style={{ fontWeight: 600 }}>{`${selectedList?.length || 0} claims`}</p>
                                                </div>
                                            }
                                        />
                                    )
                                }>
                                <img src={Images.redTrashLine} alt="" />
                            </button>
                        </div>
                    }
                />
            )}
            <div className="pending-claim-box">
                <BillingTotalBox
                    actionType={GET_PENDING_CLAIMS_TOTAL}
                    list={[
                        { title: 'Total Allowed Amount:', value: pendingClaimsTotal?.allowedAmount },
                        { title: 'Total Billed Amount:', value: pendingClaimsTotal?.billedAmount },
                    ]}
                />
                <AccordionFullTable
                    onRowClick={(item) => open(<ReceivableBillTable defaultBill={item} />)}
                    tableInfo={pendingClaimTableInfo(
                        navigate,
                        info,
                        handleAction,
                        handleCheck,
                        claimsData?.claims,
                        selectedList,
                        afterSuccess,
                    )}
                    list={claimsData?.claims}
                    listCount={claimsData?.count}
                    margin={'0'}
                    minWidth={1500}
                    selectedList={selectedList}
                />
            </div>
        </div>
    );
};
